import { create } from 'zustand';
import type { PresentationItem } from '@/types/presentation';

export interface AIMessage {
  role: 'assistant' | 'user';
  content: string;
}

export interface InvitationFields {
  name: string;
  eventType: string;
  date: string;
  time: string;
  location: string;
  dressCode: string;
  theme: string;
  primaryColor: string;
  secondaryColor: string;
  specialNotes: string;
}

const DEFAULT_FIELDS: InvitationFields = {
  name: '',
  eventType: 'Corporate',
  date: '',
  time: '',
  location: '',
  dressCode: '',
  theme: '',
  primaryColor: '#1D3557',
  secondaryColor: '#E63946',
  specialNotes: '',
};

interface InvitationEditorState extends InvitationFields {
  editingId: string | null;
  isDirty: boolean;
  backgroundImage: string | null;
  generatedImageUrl: string | null;
  logoUrl: string | null;
  aiMessages: AIMessage[];
  isGenerating: boolean;

  setField: <K extends keyof InvitationFields>(key: K, value: InvitationFields[K]) => void;
  setTheme: (themeId: string) => void;
  setBackgroundImage: (url: string | null) => void;
  setGeneratedImage: (url: string | null) => void;
  setLogoUrl: (url: string | null) => void;
  pushAiMessage: (msg: AIMessage) => void;
  setIsGenerating: (b: boolean) => void;
  setEditingId: (id: string | null) => void;
  markSaved: () => void;
  loadFromExisting: (item: PresentationItem) => void;
  reset: () => void;
}

export const useInvitationEditorStore = create<InvitationEditorState>((set) => ({
  ...DEFAULT_FIELDS,
  editingId: null,
  isDirty: false,
  backgroundImage: null,
  generatedImageUrl: null,
  logoUrl: null,
  aiMessages: [],
  isGenerating: false,

  setField: (key, value) => set({ [key]: value, isDirty: true }),

  setTheme: (themeId) => {
    set({ theme: themeId, isDirty: true });
  },

  setBackgroundImage: (url) => set({ backgroundImage: url, isDirty: true }),
  setGeneratedImage: (url) => set({ generatedImageUrl: url }),

  setLogoUrl: (url) => set({ logoUrl: url }),

  pushAiMessage: (msg) => set((s) => ({ aiMessages: [...s.aiMessages, msg] })),
  setIsGenerating: (b) => set({ isGenerating: b }),
  setEditingId: (id) => set({ editingId: id }),
  markSaved: () => set({ isDirty: false }),

  loadFromExisting: (item) => {
    const ctx = (item.context_data ?? {}) as Record<string, unknown>;
    set({
      editingId: item.id,
      name: item.name,
      eventType: typeof ctx.eventType === 'string' ? ctx.eventType : 'Corporate',
      date: typeof ctx.eventDate === 'string' ? ctx.eventDate : '',
      time: typeof ctx.eventTime === 'string' ? ctx.eventTime : '',
      location: typeof ctx.location === 'string' ? ctx.location : '',
      dressCode: typeof ctx.dressCode === 'string' ? ctx.dressCode : '',
      theme: Array.isArray(ctx.theme_slugs)
        ? ((ctx.theme_slugs as string[])[0] ?? '')
        : typeof ctx.theme_slug === 'string'
          ? ctx.theme_slug
          : typeof ctx.theme === 'string'
            ? ctx.theme
            : '',
      primaryColor: typeof ctx.primaryColor === 'string' ? ctx.primaryColor : '#1D3557',
      secondaryColor: typeof ctx.secondaryColor === 'string' ? ctx.secondaryColor : '#E63946',
      specialNotes: typeof ctx.specialNotes === 'string' ? ctx.specialNotes : '',
      generatedImageUrl: item.invitation_text ?? null,
      logoUrl:
        item.reference_images?.[0] ?? (typeof ctx.logo_url === 'string' ? ctx.logo_url : null),
      isGenerating: item.status === 'generating',
      isDirty: false,
    });
  },

  reset: () =>
    set({
      ...DEFAULT_FIELDS,
      editingId: null,
      isDirty: false,
      backgroundImage: null,
      generatedImageUrl: null,
      logoUrl: null,
      aiMessages: [],
      isGenerating: false,
    }),
}));
